import { Texture } from "pixi.js";

export interface TextureCacheOptions {
  maxSize?: number;
  crossOrigin?: "" | "anonymous" | "use-credentials";
}

/**
 * A size-limited texture cache for PixiJS.
 * This class is designed to prevent race conditions and duplicate texture loading
 * when used by multiple components concurrently.
 */
export class TextureCache {
  private textureCache: Map<string, Texture>;
  private loadingTextures: Map<string, Promise<Texture>>;
  private maxSize: number;
  private crossOrigin: "" | "anonymous" | "use-credentials";

  /**
   * Creates a new TextureCache instance.
   *
   * @param options - Configuration for the cache.
   * @param options.maxSize - The maximum number of textures to store in the cache. Defaults to 100.
   * @param options.crossOrigin - The CORS setting for images. Use "anonymous" (default) for WebGL compatibility, "" to disable CORS, or "use-credentials" for credentialed requests. Note: WebGL requires CORS to be enabled to avoid tainted canvas errors.
   */
  constructor(options: TextureCacheOptions = {}) {
    const { maxSize = 100, crossOrigin = "anonymous" } = options;

    this.textureCache = new Map<string, Texture>();
    this.loadingTextures = new Map<string, Promise<Texture>>();
    this.maxSize = maxSize;
    this.crossOrigin = crossOrigin;
  }

  /**
   * Loads a texture from the given URL. If the texture is already cached,
   * returns the cached texture. If the texture is currently being loaded,
   * returns the existing loading promise.
   *
   * @param url - The URL of the image to load.
   * @returns A promise that resolves to the loaded texture.
   */
  loadTexture(url: string): Promise<Texture> {
    // 1. Check if the texture is already in the cache.
    if (this.textureCache.has(url)) {
      return Promise.resolve(this.textureCache.get(url)!);
    }

    // 2. Check if the texture is currently being loaded.
    if (this.loadingTextures.has(url)) {
      return this.loadingTextures.get(url)!;
    }

    // 3. Check if this is a rounded corners request
    const parsedUrl = new URL(url, window.location.href);
    const roundedParam = parsedUrl.searchParams.get("rounded");
    const hasRoundedCorners = roundedParam !== null;
    const radius = hasRoundedCorners ? parseFloat(roundedParam) : 0;

    // 4. Check if service worker is active (it will handle rounded corners processing)
    const hasActiveServiceWorker =
      "serviceWorker" in navigator &&
      navigator.serviceWorker.controller !== null;

    // 5. If not cached and not loading, create a new loading promise.
    const promise = new Promise<Texture>((resolve, reject) => {
      const img = new Image();
      // Set crossOrigin for CORS support (required for WebGL/PixiJS to avoid tainted canvas)
      img.crossOrigin = this.crossOrigin;

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const context = canvas.getContext("2d");
          if (!context) {
            throw new Error("Failed to get canvas context");
          }

          // Apply rounded corners ONLY if:
          // 1. Rounded corners are requested AND
          // 2. No active service worker (otherwise SW already processed it)
          if (hasRoundedCorners && radius > 0 && !hasActiveServiceWorker) {
            console.log("IMAGE::MAIN_THREAD::ROUND_CORNERS::", url);
            // Draw the image first
            context.drawImage(img, 0, 0, img.width, img.height);

            // Apply rounded corners mask using destination-in composite operation
            context.globalCompositeOperation = "destination-in";

            // Create rounded rectangle path (same algorithm as service worker)
            const r = Math.min(radius, img.width / 2, img.height / 2);
            context.beginPath();
            context.moveTo(r, 0);
            context.lineTo(img.width - r, 0);
            context.quadraticCurveTo(img.width, 0, img.width, r);
            context.lineTo(img.width, img.height - r);
            context.quadraticCurveTo(
              img.width,
              img.height,
              img.width - r,
              img.height
            );
            context.lineTo(r, img.height);
            context.quadraticCurveTo(0, img.height, 0, img.height - r);
            context.lineTo(0, r);
            context.quadraticCurveTo(0, 0, r, 0);
            context.closePath();
            context.fill();

            console.log("IMAGE::MAIN_THREAD::ROUND_CORNERS::", url);
          } else {
            // No processing needed - just draw the image
            context.drawImage(img, 0, 0);
          }

          const texture = Texture.from(canvas);
          this.textureCache.set(url, texture);

          // Enforce cache size limit (LRU-like, by insertion order)
          if (this.textureCache.size > this.maxSize) {
            const firstKey = this.textureCache.keys().next().value;
            if (firstKey) {
              // Remove from cache but don't destroy the texture to prevent
              // issues with sprites that might still be using it
              this.textureCache.delete(firstKey);
            }
          }

          // Remove from loading map and resolve
          this.loadingTextures.delete(url);
          resolve(texture);
        } catch (error) {
          this.loadingTextures.delete(url);
          reject(error);
        }
      };

      img.onerror = (err) => {
        this.loadingTextures.delete(url);
        reject(new Error(`Failed to load image: ${url}. Error: ${err}`));
      };

      img.src = url;
    });

    // Store the promise in the loading map
    this.loadingTextures.set(url, promise);

    return promise;
  }

  /**
   * Loads a texture from the given URL with rounded corners applied.
   * The processing is attempted in the Service Worker if available,
   * otherwise it's handled on the main thread as a fallback.
   *
   * @param url - The URL of the image to load.
   * @param radius - The radius of the rounded corners in pixels.
   * @returns A promise that resolves to the loaded texture with rounded corners.
   */
  loadRoundedTexture(url: string, radius: number): Promise<Texture> {
    const processedUrl = `${url}?rounded=${radius}`;
    return this.loadTexture(processedUrl);
  }

  /**
   * Gets a texture from the cache if it exists.
   *
   * @param url - The URL of the texture to retrieve.
   * @returns The cached texture, or null if not found.
   */
  getTexture(url: string): Texture | null {
    return this.textureCache.get(url) || null;
  }

  /**
   * Gets the current number of textures in the cache.
   *
   * @returns The number of cached textures.
   */
  getCurrentSize(): number {
    return this.textureCache.size;
  }

  /**
   * Gets the maximum number of textures the cache can hold.
   *
   * @returns The maximum cache size.
   */
  getMaxSize(): number {
    return this.maxSize;
  }

  /**
   * Clears all textures from the cache. Note: textures are not destroyed
   * to prevent issues with sprites that might still be using them.
   */
  clearCache(): void {
    this.textureCache.clear();
    this.loadingTextures.clear();
  }

  /**
   * Destroys the cache. Note: cached textures are not destroyed
   * to prevent issues with sprites that might still be using them.
   */
  destroy(): void {
    this.clearCache();
  }
}
